import { ReactNode } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Trans, useTranslation } from "next-i18next";

import { useCheckIsMobile } from "@sellernote/shared/src/utils/hook";
import TextButton from "@sellernote/ui/src/sds-v2/components/button/TextButton";
import ChevronRightIcon from "@sellernote/ui/src/sds-v2/components/svgIcons/ChevronRightIcon";
import { Table } from "@sellernote/ui/src/sds-v3/components/table";

import Styled from "./index.styles";

type FaqTableData = {
  incoterms: string;
  detail: string;
  description: string;
  comment: ReactNode;
};

/**
 * 'DAP관련 질문에 대한 답변'만 데스크탑/모바일 분기가 크게 달라져서 별도 컴포넌트로 분리
 */
export default function DAPCaseAnswer() {
  const { t } = useTranslation(["page-sourcing"]);

  const { isMobile } = useCheckIsMobile();

  const columnHelper = createColumnHelper<FaqTableData>();

  const columnList = [
    columnHelper.accessor("incoterms", {
      id: "incoterms",
      header: () => t("page-sourcing:무역조건"),
      size: 60,
    }),
    columnHelper.accessor("detail", {
      id: "detail",
      header: () => t("page-sourcing:상세"),
      size: 120,
    }),
    columnHelper.accessor("description", {
      id: "description",
      header: () => t("page-sourcing:업무_범위"),
      size: 382,
    }),
    columnHelper.accessor("comment", {
      id: "comment",
      header: () => t("page-sourcing:비고"),
      size: 258,
      cell: ({ row }) => {
        return row.original.comment;
      },
    }),
  ];

  /**
   * 데스크탑과 모바일에서 동일한 문구를 구성만 달리하여 사용하므로, 상수로 만들어 사용
   */
  const 무역조건별_문구_목록 = (() => {
    return [
      {
        무역조건: t("page-sourcing:E조건"),
        상세: "EXW",
        업무범위: t(
          "page-sourcing:수입자가_수출지_공장_혹은_지정된_장소_에서부터_운송을_처리합니다."
        ),
        비고: <></>,
      },
      {
        무역조건: t("page-sourcing:F조건"),
        상세: "FOB, FCA",
        업무범위: t(
          "page-sourcing:수입자가_수출지_항구_혹은_공항_에서부터_운송을_처리합니다."
        ),
        비고: <></>,
      },
      {
        무역조건: t("page-sourcing:C조건"),
        상세: "CIF, CFR, CIP, CPT",
        업무범위: t(
          "page-sourcing:수입자가_수입지_항구_혹은_공항_에서부터_운송을_처리합니다."
        ),
        비고: (
          <>
            {isMobile ? "*" : ""}
            {t("page-sourcing:쉽다가_수입지_한국__포워더로_지정된_경우에_한정")}
            <br />
            <TextButton
              className="custom-text-button"
              theme="gray"
              fontSize={12}
              handleClick={() => {
                window.open(
                  `https://www.notion.so/shipda/C-151cfe56df9741df9fe63d478bec7a05?pvs=4`,
                  "_blank"
                );
              }}
              label={t("page-sourcing:지정하는_방법_알아보기")}
              iconInfo={{
                Icon: ChevronRightIcon,
                position: "right",
                iconHeight: 12,
                iconWidth: 12,
              }}
            />
          </>
        ),
      },
    ];
  })();

  const table = useReactTable({
    data: 무역조건별_문구_목록.map((item) => ({
      incoterms: item.무역조건,
      detail: item.상세,
      description: item.업무범위,
      comment: item.비고,
    })),
    columns: columnList,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Styled.faqWithTable>
      <div>
        <Trans
          i18nKey="page-sourcing:DAP_혹은_DDP_방식으로_상품을_구매하시더라도__할인쿠폰_혜택을_받을_수_있습니다._단__해당_인코텀즈_무역조건_의_경우__em_수출자가_수입자가_지정한_최종목적지까지_운송을_책임지는_조건_/em_이므로__수입운송_시___쉽다_의_역할이_없습니다."
          components={{ em: <em /> }}
        />

        <br />
        <br />

        <Trans i18nKey="page-sourcing:이에_할인쿠폰은_해당_건에_적용될_수는_없고__다음_거래에_활용할_수_있습니다.__쉽다_를_활용할_수_있는_수입__인코텀즈_는_다음과_같습니다." />
      </div>

      {isMobile ? (
        <div className="content-container">
          {무역조건별_문구_목록.map((item, i) => (
            <div className="content-item" key={i}>
              <div className="title">
                {item.무역조건} <em>{item.상세}</em>
              </div>

              <div className="desc">{item.업무범위}</div>
              <div className="comment">{item.비고}</div>
            </div>
          ))}
        </div>
      ) : (
        <Table>
          <Table.Header>
            {table.getHeaderGroups().map((headerGroup) => (
              <Table.HeaderGroup key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Table.HeaderColumn
                    className="custom-table-header-column"
                    key={header.id}
                    width={header.getSize()}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </Table.HeaderColumn>
                ))}
              </Table.HeaderGroup>
            ))}
          </Table.Header>

          <Table.Body>
            {table.getRowModel().rows.map((row) => (
              <Table.Row key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Table.BodyCell
                    key={cell.id}
                    className="custom-table-body-cell"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.BodyCell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </Styled.faqWithTable>
  );
}
